import dayjs from 'dayjs';

import { useMemo } from 'react';

import FormHeader from 'components/global/FormHeader';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

const DeliveryAttemptReport = () => {
	const defaultSort = useMemo(() => [{ id: 'ID', desc: true }], []);

	const columns = useMemo(
		() => [
			{
				Header: 'id',
				accessor: 'ID',
				filterType: FilterType.number,
				width: 50,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'otoOrderID',
				accessor: 'otoOrderID',
				filterType: FilterType.number,
				width: 50,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 90,
				showOnMobile: true,
				showCopyButton: true
			},
			{
				Header: 'orderDate',
				accessor: 'createdDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME,
				filterRemovable: false,
				show: true
			},
			{
				Header: 'orderTime',
				accessor: 'orderTime',
				filterType: FilterType.string,
				width: 80
			},
			{
				Header: 'pickupLocation',
				accessor: 'pickupLocation',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PICKUP_LOCATION,
				width: 90
			},
			{
				Header: 'city',
				accessor: 'cityName',
				filterType: FilterType.multiSelection
			},
			{
				Header: 'skus',
				accessor: 'skuList',
				filterType: FilterType.string,
				width: 70,
				show: false
			},
			{
				Header: 'packageSize',
				accessor: 'maxPackageSize',
				filterType: FilterType.string
			},
			{
				Header: 'shipmentNumber',
				accessor: 'shipmentNumber',
				filterType: FilterType.number,
				width: 110,
				renderCell: ListComponents.TRACKING_URL
			},
			{
				Header: 'deliveryCompany',
				accessor: 'deliveryName',
				filterType: FilterType.enumMulti,
				width: 90,
				enumName: ENUMS.DELIVERY_COMPANIES_USED,
				renderCell: ListComponents.DC_LOGO
			},
			{
				Header: 'status',
				accessor: 'status',
				renderCell: ListComponents.STATUS,
				filterType: FilterType.enumMulti,
				enumName: ENUMS.ALL_STATUSES,
				filterPrefix: 'status.',
				width: 100,
				showOnMobile: true
			},
			{
				Header: 'shipmentCreatedDate',
				accessor: 'shipmentCreatedDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'pickupDate',
				accessor: 'pickupDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{ Header: 'pickupTime', accessor: 'pickupTime', filterType: FilterType.string, width: 80 },
			{
				Header: 'firstDeliveryAttemptDate',
				accessor: 'firstDeliveryAttemptDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'secondAttemptDate',
				accessor: 'secondAttemptDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'lastAttemptDate',
				accessor: 'lastAttemptDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'attemptCount',
				accessor: 'attemptCount',
				filterType: FilterType.number,
				width: 80
			},
			{
				Header: 'deliveryDate',
				accessor: 'deliveryDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{ Header: 'deliveryTime', accessor: 'deliveryTime', filterType: FilterType.string, width: 80 },
			{
				Header: 'returnDate',
				accessor: 'returnDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME,
				show: false
			},
			{
				Header: 'canceledDate',
				accessor: 'canceledDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME,
				show: false
			},
			{
				Header: 'isRescheduled',
				accessor: 'isRescheduled',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				show: false
			},
			{ Header: 'preparationTime', accessor: 'preparationTime', filterType: FilterType.string },
			{ Header: 'fulfillmentTime', accessor: 'fulfillmentTime', filterType: FilterType.string },
			{ Header: 'pickupDifferenceHours', accessor: 'pickupDifference', filterType: FilterType.string },
			{ Header: 'attemptTimeDifference', accessor: 'attemptTimeDifference', filterType: FilterType.string },
			{ Header: 'deliveryDifferenceHours', accessor: 'deliveryDifference', filterType: FilterType.string },
			{ Header: 'tripDifference', accessor: 'tripDifference', filterType: FilterType.string, show: false },

			{
				Header: 'deliveryTimeDifferenceFromFirstAttempt',
				accessor: 'deliveryTimeDifferenceFromFirstAttempt',
				filterType: FilterType.string,
				show: false
			},

			{
				Header: 'deliveryDifferenceFromLastAttempt',
				accessor: 'deliveryDifferenceFromLastAttempt',
				filterType: FilterType.string,
				show: false
			},
			{ Header: 'orderDescription', accessor: 'orderDescription', filterType: FilterType.string, show: false },
			{ Header: 'deliveryComment', accessor: 'deliveryComment', filterType: FilterType.string, show: false },
			{
				Header: 'comment',
				accessor: 'failedDeliveryComment',
				filterType: FilterType.string,
				show: false
			},
			{ Header: 'driverMobilyID', accessor: 'driverMobilyID', filterType: FilterType.string, show: false },
			{ Header: 'driverDepartment', accessor: 'driverDepartment', filterType: FilterType.string, show: false },
			{ Header: 'driverCity', accessor: 'driverCity', filterType: FilterType.multiSelection, show: false },
			{
				Header: 'maxTier',
				accessor: 'maxTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DC_CITY_TIER
			},

			{
				Header: 'shippingAmount',
				accessor: 'shippingAmount',
				filterType: FilterType.float,
				width: 120,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'ref1',
				accessor: 'ref1',
				filterType: FilterType.string,
				width: 90,
				show: false,
				renderCell: ListComponents.URL,
				roleName: '/column/ref1'
			},
			{
				Header: 'ref2',
				accessor: 'ref2',
				filterType: FilterType.string,
				width: 90,
				show: false,
				renderCell: ListComponents.URL,
				roleName: '/column/ref2'
			},
			{
				Header: 'ref3',
				accessor: 'ref3',
				filterType: FilterType.string,
				width: 90,
				show: false,
				renderCell: ListComponents.URL,
				roleName: '/column/ref3'
			},
			{
				Header: 'pickupCutoffTime',
				accessor: 'pickupCutoffTime',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'warehouseID',
				accessor: 'warehouseID',
				filterType: FilterType.string,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'branchIDNumber',
				accessor: 'branchID',
				filterType: FilterType.string,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'deliveryCompanyID',
				accessor: 'deliveryCompanyID',
				filterType: FilterType.number,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'DCSettingID',
				accessor: 'DCSettingID',
				filterType: FilterType.number,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'customerAddress',
				accessor: 'customerAddress',
				filterType: FilterType.string
			},
			{
				Header: 'areaName',
				accessor: 'areaName',
				filterType: FilterType.string
			},
			{
				Header: 'grandTotal',
				accessor: 'orderGrandTotal',
				filterType: FilterType.number
			},
			{
				Header: 'productName',
				accessor: 'productName',
				filterType: FilterType.string
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: columns[3],
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[]
	);

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					lightTitle="reports"
					boldTitle="deliveryAttemptReport"
					icon={faArrowLeft}
					backUrl={mergePaths(PATHS.REPORTS, PATHS.SHIPMENT_REPORTS)}
				/>
			</HeaderContainerFormHeader>

			<DynamicList
				id={DYNAMICLISTS.DELIVERY_ATTEMPT_REPORT}
				columns={columns}
				fetchUrl={REPORT.LIST.GET_DELIVERY_ATTEMPT_REPORT_LIST}
				defaultSort={defaultSort}
				defaultFilters={defaultFilters}
				exportName="deliveryAttemptReport"
			/>
		</div>
	);
};

export default DeliveryAttemptReport;
